import { z } from "zod";

export const passwordAuthTypeSchema = z.object({ id: z.literal(0), name: z.literal("Password") });
export const oidcAuthTypeSchema = z.object({ id: z.literal(1), name: z.literal("Oidc") });
export const samlAuthTypeSchema = z.object({ id: z.literal(2), name: z.literal("Saml") });
export const mixedAuthTypeSchema = z.object({ id: z.literal(3), name: z.literal("Mixed") });
export const passwordAndOidcAuthTypeSchema = z.object({ id: z.literal(4), name: z.literal("PasswordAndOidc") });
export const passwordAndSamlAuthTypeSchema = z.object({ id: z.literal(5), name: z.literal("PasswordAndSaml") });
export const oidcAndSamlAuthTypeSchema = z.object({ id: z.literal(6), name: z.literal("OidcAndSaml") });

export const authTypeSchema = z.union([
	passwordAuthTypeSchema,
	oidcAuthTypeSchema,
	samlAuthTypeSchema,
	mixedAuthTypeSchema,
	passwordAndOidcAuthTypeSchema,
	passwordAndSamlAuthTypeSchema,
	oidcAndSamlAuthTypeSchema,
]);

export const tenantStatusSchema = z.object({
	id: z.number(),
	name: z.string(),
});

export const tenantMetaSchema = z
	.object({
		tenantId: z.string(),
		company: z.string(),
		salesForceId: z.string(),
		tenantStatus: tenantStatusSchema,
		companyId: z.string().nullish(),
		siteTimeZoneOffset: z.number().optional(),
		siteTimeZone: z.string().optional(),
		siteTimeZoneLabel: z.string().optional(),
		siteTimeZoneAltName: z.string().optional(),
		siteTimeZoneAbbreviation: z.string().optional(),
		expiresAt: z.coerce.date().nullish(),
	})
	.passthrough();

export const ssoProviderSchema = z
	.object({
		name: z.string(),
		id: z.number(),
	})
	.passthrough();

export const ssoMetaSchema = z
	.object({
		$type: z.string().default(""),
		authority: z.string().nullable().default(null),
		clientId: z.string().nullable().default(null),
		idpIdentifier: z.string(),
		isEnabled: z.boolean(),
		isOidc: z.boolean(),
		jwtProvider: ssoProviderSchema,
		type: z.string().default(""),
		wellKnown: z.string().nullable().default(null),
		ssoDomain: z.string().nullable().default(null),
	})
	.passthrough();

export const companySchema = z
	.object({
		id: z.string(),
		name: z.string(),
		tenantList: z
			.array(tenantMetaSchema)
			.nullish()
			.transform((value) => value ?? []),
		authType: authTypeSchema.default({ id: 0, name: "Password" }),
		ssoMetaList: z
			.array(ssoMetaSchema)
			.nullish()
			.transform((value) => value ?? []),
		ssoDomain: z.string().nullable().default(null),
		disablePasswordLogin: z.boolean().default(false),
	})
	.passthrough();

export type AuthType = z.infer<typeof authTypeSchema>;
export type TenantMeta = z.infer<typeof tenantMetaSchema>;
export type SsoMeta = z.infer<typeof ssoMetaSchema>;
export type Company = z.infer<typeof companySchema>;
